import React, { useEffect, useState } from "react";
import config from "../../config";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Button,
  Chip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";

const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";

export default function ItemReport() {

  const [items,setItems] = useState([]);
  const [loading,setLoading] = useState(true);

  const [searchItem,setSearchItem] = useState("");
  const [searchCategory,setSearchCategory] = useState("");

  const [page,setPage] = useState(0);
  const [rowsPerPage,setRowsPerPage] = useState(10);

  const [sortField,setSortField] = useState("name");
  const [sortDir,setSortDir] = useState("asc");

  const [editOpen,setEditOpen] = useState(false);
  const [editItem,setEditItem] = useState(null);

  const token = sessionStorage.getItem("token");

  const fetchItems = async () => {
    setLoading(true);

    const res = await fetch(
      `${config.API_BASE_URL}/home/Getallrestitem/${company?.id}`,
      { headers:{Authorization:`Bearer ${token}`} }
    );

    const data = await res.json();
    setItems(data || []);
    setLoading(false);
  };

  useEffect(()=>{
    fetchItems();
  },[]);


  /* -------------------- FILTER -------------------- */

  const filtered = items.filter(x =>
      x.name.toLowerCase().includes(searchItem.toLowerCase()) &&
      x.categoryName.toLowerCase().includes(searchCategory.toLowerCase())
  );


  /* -------------------- SORT -------------------- */

  const sorted = [...filtered].sort((a,b)=>{

    if(sortField==="price")
      return sortDir==="asc" ? a.price-b.price : b.price-a.price;

    const valA = a[sortField].toLowerCase();
    const valB = b[sortField].toLowerCase();

    if(valA < valB) return sortDir==="asc" ? -1 : 1;
    if(valA > valB) return sortDir==="asc" ? 1 : -1;

    return 0;
  });


  const handleSort=(field)=>{

    if(sortField===field){
      setSortDir(sortDir==="asc"?"desc":"asc");
    }else{
      setSortField(field);
      setSortDir("asc");
    }
  };


  /* -------------------- PAGINATION -------------------- */

  const paged = sorted.slice(
    page*rowsPerPage,
    page*rowsPerPage + rowsPerPage
  );


  /* -------------------- EXPORT -------------------- */

  const exportToExcel = () => {

    const formatted = filtered.map((i,index)=>({
      "#":index+1,
      "Item":i.name,
      "Category":i.categoryName,
      "Price":i.price,
      "Status":i.isActive?"Active":"Inactive"
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb,ws,"Items");

    XLSX.writeFile(wb,"ItemReport.xlsx");
  };


  /* -------------------- STATUS TOGGLE -------------------- */

  const toggleStatus = async (id) => {

  await fetch(
    `${config.API_BASE_URL}/home/Deactiveitem/${id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  fetchItems();
};


  /* -------------------- UPDATE -------------------- */

  const openEdit=(item)=>{
    setEditItem({...item});
    setEditOpen(true);
  };


const updateItem = async () => {

  await fetch(
    `${config.API_BASE_URL}/home/Updaterestitem/${editItem.name}/${editItem.price}/${editItem.id}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  setEditOpen(false);
  fetchItems();
};


  const categories = [...new Set(items.map(x=>x.categoryName))];


  if(loading)
    return(
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress size={60}/>
      </Box>
    );


  return(

  <Box sx={{p:4}}>

    <Typography variant="h4" fontWeight="bold" mb={3} textAlign="center">
      Restaurant Item Report
    </Typography>


    {/* SUMMARY CARD */}

    <Grid container spacing={3} mb={4}>
      <Grid item xs={12} md={3}>
        <Paper sx={{p:3,textAlign:"center"}}>
          <Typography>Total Items</Typography>
          <Typography variant="h5">{filtered.length}</Typography>
        </Paper>
      </Grid>
    </Grid>


    {/* FILTERS */}

    <Box display="flex" gap={2} mb={3} flexWrap="wrap">

      <TextField
        label="Search Item"
        size="small"
        value={searchItem}
        onChange={e=>setSearchItem(e.target.value)}
      />

      <TextField
        select
        label="Category"
        size="small"
        value={searchCategory}
        onChange={e=>setSearchCategory(e.target.value)}
        sx={{minWidth:180}}
      >
        <MenuItem value="">All</MenuItem>

        {categories.map((c,i)=>(
          <MenuItem key={i} value={c}>{c}</MenuItem>
        ))}

      </TextField>

      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon/>}
        onClick={exportToExcel}
      >
        Export
      </Button>

    </Box>



    {/* TABLE */}

    <Paper>

      <TableContainer>

        <Table>

          <TableHead>

            <TableRow>

              <TableCell>#</TableCell>

              <TableCell onClick={()=>handleSort("name")} sx={{cursor:"pointer"}}>
                Item
              </TableCell>

              <TableCell onClick={()=>handleSort("categoryName")} sx={{cursor:"pointer"}}>
                Category
              </TableCell>

              <TableCell onClick={()=>handleSort("price")} sx={{cursor:"pointer"}}>
                Price
              </TableCell>
              

              <TableCell>Status</TableCell>

              <TableCell>Actions</TableCell>

            </TableRow>

          </TableHead>


          <TableBody>

            {paged.map((item,index)=>(
              <TableRow key={item.id}>

                <TableCell>{page*rowsPerPage+index+1}</TableCell>

                <TableCell>{item.name}</TableCell>

                <TableCell>{item.categoryName}</TableCell>

                <TableCell>{Companycurrency}{item.price}</TableCell>

                <TableCell>

                  <Chip
                    label={item.isActive?"Active":"Inactive"}
                    color={item.isActive?"success":"error"}
                    size="small"
                  />

                </TableCell>

                <TableCell>

                  <Button
                    size="small"
                    variant="contained"
                    color={item.isActive?"warning":"success"}
                    onClick={()=>toggleStatus(item.id,item.isActive)}
                    sx={{mr:1}}
                  >
                    {item.isActive?"Deactivate":"Activate"}
                  </Button>


                  <Button
                    size="small"
                    variant="contained"
                    onClick={()=>openEdit(item)}
                  >
                    Update
                  </Button>

                </TableCell>

              </TableRow>
            ))}

          </TableBody>

        </Table>

      </TableContainer>


      {/* PAGINATION */}

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e,newPage)=>setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e)=>{
          setRowsPerPage(parseInt(e.target.value,10));
          setPage(0);
        }}
      />

    </Paper>



    {/* UPDATE MODAL */}

    <Dialog open={editOpen} onClose={()=>setEditOpen(false)}>

      <DialogTitle>Update Item</DialogTitle>

      <DialogContent>

        <TextField
          label="Item Name"
          fullWidth
          margin="dense"
          value={editItem?.name || ""}
          onChange={e=>setEditItem({...editItem,name:e.target.value})}
        />

        <TextField
          label="Price"
          type="number"
          fullWidth
          margin="dense"
          value={editItem?.price || ""}
          onChange={e=>setEditItem({...editItem,price:e.target.value})}
        />

      </DialogContent>

      <DialogActions>

        <Button onClick={()=>setEditOpen(false)}>Cancel</Button>

        <Button variant="contained" onClick={updateItem}>
          Save
        </Button>

      </DialogActions>

    </Dialog>


  </Box>

  );
}
